import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { ReplaceStep } from 'prosemirror-transform';
import { Decoration, DecorationSet } from 'prosemirror-view';

// Export the plugin key
export const pluginKey = new PluginKey('slashTriggerStyling');

// Helper to create the decoration
function createSlashDecoration(doc, position) {
  const slashPos = position - 1; // Position of the inserted '/'

  const decoration = Decoration.inline(
    slashPos,
    position,
    {
      // Decorate just the '/'
      class: 'custom-trigger-element',
      inclusiveStart: false,
      inclusiveEnd: false,
    },
    {
      key: `slash-trigger-${slashPos}`,
    }
  );

  return DecorationSet.create(doc, [decoration]);
}

// Create the Tiptap extension
export const SlashTriggerStyling = Extension.create({
  name: 'slashTriggerStyling',

  // Add the ProseMirror plugin
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pluginKey,
        state: {
          // Initialize with empty decorations
          init() {
            return DecorationSet.empty;
          },
          // Apply changes: re-scan ONLY on specific conditions
          apply(tr, oldState) {
            // If only the selection changed (no document change)
            if (tr.selectionSet && !tr.docChanged) {
              // Cursor moved, remove any existing decoration from this plugin
              return DecorationSet.empty;
            }

            // If the document didn't change (and selection didn't change), keep old state
            if (!tr.docChanged) {
              return oldState;
            }

            // --- Document Changed ---
            let slashInsertedAt = -1; // Position *after* a validly inserted slash

            // Check the transaction steps for '/' insertion at the cursor position
            tr.steps.forEach(step => {
              // We only care about ReplaceStep where text was inserted
              if (
                step instanceof ReplaceStep &&
                step.slice.size > 0 &&
                step.from === step.to
              ) {
                // Check the inserted content specifically
                const insertedSlice = step.slice;
                if (
                  insertedSlice.content.childCount === 1 &&
                  insertedSlice.content.firstChild.isText
                ) {
                  const insertedText = insertedSlice.content.firstChild.text;
                  if (insertedText === '/') {
                    // Check if the selection is a cursor right after the insertion
                    if (
                      tr.selection.empty &&
                      tr.selection.$anchor.pos === step.from + 1
                    ) {
                      // Check context: character after '/' must be space or end of node/doc
                      const posAfterSlash = step.from + 1;
                      const charAfterSlash = tr.doc.textBetween(
                        posAfterSlash,
                        posAfterSlash + 1,
                        '',
                        ''
                      );

                      // Check if it's effectively at the end of the parent block node
                      const $posAfterSlash = tr.doc.resolve(posAfterSlash);
                      const isAtEndOfBlock =
                        $posAfterSlash.parentOffset ===
                        $posAfterSlash.parent.content.size;

                      // Trigger ONLY if followed by space, or nothing (end of text/block)
                      if (
                        charAfterSlash === ' ' ||
                        charAfterSlash === '' ||
                        isAtEndOfBlock
                      ) {
                        slashInsertedAt = posAfterSlash; // Store the position *after* the slash
                      }
                    }
                  }

                  if (insertedText === '/‎') {
                    slashInsertedAt = step.from + 1;
                  }
                }
              }
            });

            // If a valid slash trigger was inserted
            if (slashInsertedAt !== -1) {
              // Create the decoration for this specific instance
              return createSlashDecoration(tr.doc, slashInsertedAt);
            }

            // Doc changed, but not in a way that creates/sustains the trigger
            // -> Remove any existing decoration from this plugin
            return DecorationSet.empty;
          },
        },
        props: {
          // Provide the decorations to the editor view
          decorations(state) {
            // Retrieve the state using the plugin key
            return pluginKey.getState(state);
          },
        },
      }),
    ];
  },
});

// Export the extension for use
export default SlashTriggerStyling;
